import React from 'react';
import { Button, Typography, Accordion, AccordionSummary, AccordionDetails, List, ListItem } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { IDeliveryType } from '../../models/configuration/plugin/IConfiguration';
import { DELIVERY_PREFIX } from '../../utils/constants';
import { useSolexEditor } from '../../hooks/useSolexEditor';

interface SolexDisplayProps {
    solexEditorLink: string | null;
    warnings: string[] | null;
    deliveryMethods: IDeliveryType[];
    solexEditor: Pick<ReturnType<typeof useSolexEditor>, 'isEnabled' | 'isOpeningEditor' | 'openManually'>;
}

/**
 * Displays Solex Document Editor button or warnings in the success dialog.
 * Only shown for eSign and eClose delivery methods when feature flag is enabled.
 * Receives solexEditor object from parent to ensure consistent hook instance with alertHandler.
 */
export const SolexDisplay: React.FC<SolexDisplayProps> = ({
    solexEditorLink,
    warnings,
    deliveryMethods,
    solexEditor
}) => {

    // Check if any delivery method is Solex-eligible (eSign and eClose and their children ONLY)
    // Normalize to lowercase to handle mixed casing (consistent with OrderButton.tsx pattern)
    const hasSolexEligibleDeliveryMethod = deliveryMethods.some(dm =>
        dm.Id?.toLowerCase().startsWith(DELIVERY_PREFIX.ESIGN) ||
        dm.Id?.toLowerCase().startsWith(DELIVERY_PREFIX.ECLOSE)
    );

    // Don't render if feature flag disabled or no eligible delivery methods
    if (!solexEditor.isEnabled || !hasSolexEligibleDeliveryMethod) {
        return null;
    }

    // Mode 1: Show Solex button when link is available
    if (solexEditorLink) {
        return (
            <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f5f5f5', border: '1px solid #e0e0e0', borderRadius: '4px' }}>
                <Typography variant="body2" style={{ marginBottom: '15px' }}>
                    Your Solex Document Editor session is ready. Click below to open:
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={async () => {
                        await solexEditor.openManually(solexEditorLink);
                    }}
                    disabled={solexEditor.isOpeningEditor}
                    fullWidth
                >
                    {solexEditor.isOpeningEditor ? 'Opening...' : 'Open Solex Document Editor'}
                </Button>
            </div>
        );
    }

    // Mode 2: Show warnings accordion when link is null but warnings exist
    if (!solexEditorLink && warnings && warnings.length > 0) {
        return (
            <div style={{ marginTop: '20px' }}>
                <Accordion defaultExpanded={false}>
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{
                            backgroundColor: '#fff3e0', // Light amber background
                            border: '1px solid #ffb74d', // Amber border
                            borderRadius: '4px'
                        }}
                    >
                        <Typography
                            component="span"
                            sx={{
                                color: '#f57c00', // Darker amber text
                                fontWeight: 500
                            }}
                        >
                            Solex Warnings ({warnings.length})
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ backgroundColor: '#fffaf0' }}>
                        <List>
                            {warnings.map((warning, i) => (
                                <ListItem key={`warning-${i}`} disablePadding>
                                    <Typography variant="body2">
                                        • {warning}
                                    </Typography>
                                </ListItem>
                            ))}
                        </List>
                    </AccordionDetails>
                </Accordion>
            </div>
        );
    }

    // No link and no warnings: render nothing
    return null;
};
